import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, FileText, Download, ArrowRight } from "lucide-react";

import { Button } from "@/ui/components/Button";
import { Card, CardContent } from "@/ui/components/Card";
import { Badge } from "@/ui/components/Badge";
import { api, DOCUMENT_LABEL, type DocumentKind } from "@/lib/api";

// Temporarily hidden — flip to `true` to show the AP / AR sample PDF
// download cards on the post-submit screen. The backend renderers
// (app/pdfs.py) and the GET /api/submissions/{id}/documents/{kind}
// route remain wired up, so admins can still fetch sample PDFs by URL.
const SHOW_SAMPLE_DOCS = false;

const DOCS: { kind: DocumentKind; description: string }[] = [
  { kind: "ap", description: "How a vendor bill would look in TrueCost based on your answers." },
  { kind: "ar", description: "How an owner invoice would look — branded with Property Meld." },
];

export function Success() {
  const { id = "" } = useParams();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const submission = useQuery({
    queryKey: ["submission", id],
    queryFn: () => api.getSubmission(id),
    enabled: Boolean(id),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Card>
        <CardContent className="space-y-4 p-8">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-7 w-7 shrink-0 text-success" />
            <div>
              <h1 className="font-display text-2xl font-semibold leading-tight">
                Thanks for sharing how you bill
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Your responses are saved. We'll be in touch if we have follow-up questions —
                appreciate the help making TrueCost better.
              </p>
            </div>
          </div>

          {submission.data && (
            <div className="flex flex-wrap items-center gap-2 pt-2 text-xs text-muted-foreground">
              <Badge variant="muted">
                Submission {submission.data.id.slice(0, 8)}
              </Badge>
              {submission.data.punchout.company && (
                <Badge variant="muted">{submission.data.punchout.company}</Badge>
              )}
              <span>
                Saved {new Date(submission.data.created_at).toLocaleString()}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {SHOW_SAMPLE_DOCS && (
        <div className="grid gap-4 sm:grid-cols-2">
          {DOCS.map(({ kind, description }) => (
            <Card key={kind}>
              <CardContent className="flex h-full flex-col gap-3 p-6">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {kind.toUpperCase()}
                  </span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-tight">
                  {DOCUMENT_LABEL[kind]}
                </h3>
                <p className="flex-1 text-sm text-muted-foreground">{description}</p>
                <div className="flex gap-2">
                  <Button asChild variant="outline" size="sm">
                    <a href={api.documentUrl(id, kind)} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </Button>
                  <Button asChild size="sm">
                    <a href={api.documentUrl(id, kind)} download={`truecost-${kind}.pdf`}>
                      <Download /> Download
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {me.data?.is_admin && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
            <div>
              <h3 className="font-display text-base font-semibold leading-tight">
                Admin tools
              </h3>
              <p className="text-sm text-muted-foreground">
                Browse all submissions and export them to CSV.
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to="/admin">
                Go to admin <ArrowRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
